/**
 * GreenSyn Inventory — shared enums.
 *
 * These string unions are the contract between the API and the web app.
 * They mirror the Prisma enums in apps/api/prisma/schema.prisma and the
 * varchar constraints in db/migrations. Change one, change all three.
 */

/**
 * Who a user is, for role-based access control.
 *
 * There is deliberately no role enum here. Roles became rows in the `roles`
 * table so an administrator can define their own; the built-in ones are
 * described by `SYSTEM_ROLES` in auth.ts, and capabilities — not role names —
 * are what the code checks.
 */

/** What kind of thing an item is. Drives UI grouping and default behaviour. */
export const ITEM_KINDS = [
  'raw_material',
  'packaging',
  'intermediate',
  'finished_good',
  'other',
] as const;
export type ItemKind = (typeof ITEM_KINDS)[number];

/** Where a lot physically sits. */
export const LOCATION_TYPES = ['warehouse', 'production', 'quarantine', 'scrap', 'lab'] as const;
export type LocationType = (typeof LOCATION_TYPES)[number];

/**
 * How a supplier is registered for GST.
 *
 * `unregistered` is a statement about the supplier — they have no GSTIN and
 * issue no tax invoice, so there is no input credit. It is deliberately not the
 * same as leaving the field blank, which means "not recorded yet": a supplier
 * being onboarded honestly has neither, and forcing a wrong answer blocks the
 * work until their certificate arrives.
 */
export const GST_REGISTRATION_TYPES = ['regular', 'composition', 'unregistered'] as const;
export type GstRegistrationType = (typeof GST_REGISTRATION_TYPES)[number];

/**
 * How a lot came to exist. `recovered` is the tag that marks raw material
 * reclaimed from a production batch (see docs/PLAN.md section 9).
 */
export const LOT_ORIGINS = ['purchased', 'produced', 'recovered', 'adjusted'] as const;
export type LotOrigin = (typeof LOT_ORIGINS)[number];

/** Whether a lot may be consumed. `quarantine` lots exist but cannot be drawn. */
export const LOT_STATUSES = ['available', 'quarantine', 'rejected', 'consumed', 'scrapped'] as const;
export type LotStatus = (typeof LOT_STATUSES)[number];

/** Ledger entry types. The ledger is append-only; corrections are reversals. */
export const MOVEMENT_TYPES = [
  'receipt',
  'issue',
  'consume',
  'output',
  'recovery',
  'transfer_out',
  'transfer_in',
  'adjust',
  'scrap',
] as const;
export type MovementType = (typeof MOVEMENT_TYPES)[number];

/**
 * How each movement type affects stock.
 *
 * `either` matters: an adjustment is a correction in whichever direction
 * reality turned out to be — a stock count can find extra material just as
 * easily as a shortfall — so the caller must state the direction.
 */
export const MOVEMENT_DIRECTIONS: Record<MovementType, 'in' | 'out' | 'either'> = {
  receipt: 'in',
  issue: 'out',
  consume: 'out',
  output: 'in',
  recovery: 'in',
  transfer_out: 'out',
  transfer_in: 'in',
  adjust: 'either',
  scrap: 'out',
};

/** 1 increases stock, -1 decreases it, 0 means the caller decides. */
export function movementSign(type: MovementType): 1 | -1 | 0 {
  const direction = MOVEMENT_DIRECTIONS[type];
  if (direction === 'in') return 1;
  if (direction === 'out') return -1;
  return 0;
}

/** Production batch lifecycle. */
export const BATCH_STATUSES = ['draft', 'in_progress', 'completed', 'closed', 'cancelled'] as const;
export type BatchStatus = (typeof BATCH_STATUSES)[number];

/** Where a batch consumption line came from. */
export const CONSUMPTION_SOURCES = ['recipe', 'adhoc', 'substitute'] as const;
export type ConsumptionSource = (typeof CONSUMPTION_SOURCES)[number];

/**
 * Recovered-material release policy. `quarantine` requires a QC pass before the
 * recovered lot can be consumed; `immediate` makes it usable at once.
 */
export const RELEASE_POLICIES = ['quarantine', 'immediate'] as const;
export type ReleasePolicy = (typeof RELEASE_POLICIES)[number];

/** How a recovered lot is valued. */
export const RECOVERY_CREDIT_POLICIES = [
  'percent_of_input_cost',
  'latest_cost',
  'manual',
] as const;
export type RecoveryCreditPolicy = (typeof RECOVERY_CREDIT_POLICIES)[number];

/** Recipe version approval state. A batch pins one version. */
export const RECIPE_STATUSES = ['draft', 'approved', 'retired'] as const;
export type RecipeStatus = (typeof RECIPE_STATUSES)[number];

/**
 * How a component's consumption tolerance is expressed. Both are supported and
 * chosen per component: a percentage suits bulk material (2% of 250 kg), an
 * absolute quantity suits discrete items (+/- 2 bottles).
 */
export const TOLERANCE_TYPES = ['percent', 'absolute'] as const;
export type ToleranceType = (typeof TOLERANCE_TYPES)[number];

/** Quality control outcomes. */
export const QC_STATUSES = ['pending', 'passed', 'failed'] as const;
export type QcStatus = (typeof QC_STATUSES)[number];

/** What a quality check applies to. */
export const QC_SUBJECT_TYPES = ['lot', 'recovery', 'batch'] as const;
export type QcSubjectType = (typeof QC_SUBJECT_TYPES)[number];
